import FadeProducer from '@src/fadeEffectProcessor/FadeProducer';
import { useEditor, useLayoutStore, useVideoSource } from '@src/stores-v2';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import CutIcon from '@src/svgs/CutIcon';
import DeleteIcon from '@src/svgs/DeleteIcon';
import TrackDownIcon from '@src/svgs/TrackDownIcon';
import TrackUpIcon from '@src/svgs/TrackUpIcon';
import ZoomMinusIcon from '@src/svgs/ZoomMinusIcon';
import ZoomPlusIcon from '@src/svgs/ZoomPlusIcon';
import { deepClone } from '@src/utility/deepClone';
import { moveUpToHiddenParent } from '@src/utility/stackedLayout';
import { throttle } from 'lodash';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { useCallback } from 'react';
import styled, { css } from 'styled-components';

import { KARAOKE_TRACK_NUMBER } from '../../stores/VideoCreatorStore';
import { MediaCard, SidebarOption } from '../../types.ts/general';
import { getDocumentHeight } from '../../utility/general';
import PlainSlider from '../common/PlainSlider';

import type { Element } from '@src/editor';

const ZOOM_MULTIPLIER = 1.5;
const logBase = (n: number) => Math.log(n) / Math.log(ZOOM_MULTIPLIER);

const SCALE_FACTOR = 1000000;
const toDisplayScale = (value: number) => Math.round(value * SCALE_FACTOR);
const toInternalScale = (value: number) => value / SCALE_FACTOR;

const Main = styled.div`
  height: 36px;
  padding: 5px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 30px;
`;

function ArrowDownIcon() {
  return (
    <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
      <path d="M9 5.5L5 1.5L1 5.5" stroke="#F2D093" strokeWidth="2" />
    </svg>
  );
}

const ArrowDown = styled.div<{ rotate: boolean }>`
  transform: ${props => props.rotate && 'rotate(180deg)'};
  display: flex;
  align-items: center;
`;

const LeftActions = styled.div`
  display: flex;
  gap: 8px;
  display: flex;
  align-items: center;
`;

const Action = styled.button<{ disabled?: boolean; hover?: boolean }>`
  background-color: transparent;
  outline: 0;
  border: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${props => (props.disabled ? '#848484' : '#F2D093')};
  font-size: 12px;
  font-weight: 500;
  line-height: normal;
  cursor: pointer;
  padding: 8px;
  svg {
    width: 14px;
    height: 14px;
    path {
      transition: 0.15s;
    }
  }
  ${props =>
    props.hover &&
    css`
      &:active {
        svg path {
          opacity: 0.6;
        }
      }
      &:hover {
        svg path {
          fill: #f2d093;
        }
      }
    `}
`;

const ToggleHide = styled.div``;

const VisibilityStatus = styled.span`
  font-size: 12px;
  line-height: normal;
  font-weight: 500;
  color: #484848;
  pointer-events: none;
`;

const ZoomControl = styled.div`
  display: flex;
  align-items: center;
  width: 160px;
`;

const TimelineControls = observer(() => {
  const videoCreator = useVideoCreatorStore();
  const videoSource = useVideoSource();
  const editorStore = useEditor();
  const layoutStore = useLayoutStore();
  const percentToPixel =
    parseFloat(layoutStore.timelineHeight || '0%') * 0.01 * getDocumentHeight();

  const toggleTimeline = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.stopPropagation();
    if (percentToPixel > 40) {
      layoutStore.setTimelineHeight((36 * 100) / getDocumentHeight());
    } else {
      layoutStore.setTimelineHeight(30);
    }
  };

  const onZoomChange = useCallback(
    throttle((value: number) => {
      runInAction(() => {
        videoCreator.timelineScale = value;
      });
    }, 300),
    [],
  );

  const minScale = videoCreator.defaultTimelineScale * 0.2;
  const maxLogScale = logBase(videoCreator.maxTimelineScale / minScale);
  const currLogScale = logBase(videoCreator.timelineScale / minScale);
  const currScale = Math.min(currLogScale, maxLogScale);
  const isElementSelected = videoCreator.activeElementIds.length > 0;

  const handleLayerUp = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const activeElement = videoCreator.getActiveElement();
    if (activeElement) {
      if (editorStore.headlessEnabled) {
        const trackId = activeElement.track.toString();
        editorStore.shiftTrackLayerUp(trackId);
      } else {
        await videoCreator.trackManager.rearrangeTracks(
          activeElement.track,
          'up',
        );
      }
    }
  };

  const handleLayerDown = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const activeElement = videoCreator.getActiveElement();
    if (activeElement) {
      if (editorStore.headlessEnabled) {
        const trackId = activeElement.track.toString();
        editorStore.shiftTrackLayerDown(trackId);
      } else {
        await videoCreator.trackManager.rearrangeTracks(
          activeElement.track,
          'down',
        );
      }
    }
  };

  return (
    <Main
      onMouseDown={e => {
        e.stopPropagation();
      }}
    >
      <LeftActions>
        <ZoomControl>
          <Action
            hover
            onClick={() => {
              if (videoCreator.timelineScale <= minScale) return;
              onZoomChange(
                Math.max(
                  videoCreator.timelineScale / ZOOM_MULTIPLIER,
                  minScale,
                ),
              );
            }}
          >
            <ZoomMinusIcon />
          </Action>
          <PlainSlider
            min={0}
            max={toDisplayScale(maxLogScale)}
            currValue={toDisplayScale(currScale)}
            getValue={(value: number) => {
              // Convert from logarithmic slider value back to actual scale
              const actualScale =
                minScale * ZOOM_MULTIPLIER ** toInternalScale(value);
              onZoomChange(actualScale);
            }}
          />
          <Action
            hover
            onClick={() => {
              if (videoCreator.timelineScale >= videoCreator.maxTimelineScale)
                return;
              const newScale = Math.min(
                videoCreator.timelineScale * ZOOM_MULTIPLIER,
                videoCreator.maxTimelineScale,
              );
              // If we're very close to max, just set to max exactly
              onZoomChange(
                newScale > videoCreator.maxTimelineScale * 0.95
                  ? videoCreator.maxTimelineScale
                  : newScale,
              );
            }}
          >
            <ZoomPlusIcon />
          </Action>
        </ZoomControl>
        <Action
          aria-label="cut-track"
          disabled={!isElementSelected}
          onClick={async e => {
            e.stopPropagation();
            if (editorStore.headlessEnabled) {
              const activeElement = videoCreator.getActiveElement();
              if (!activeElement) {
                return;
              }

              await videoCreator.reframingModeManager.exitReframingMode();

              let elementToSelect: Element | undefined;
              const activeLayoutElement =
                videoCreator.stackedLayoutManager.getLayoutElementFor(
                  activeElement,
                );
              if (activeLayoutElement) {
                if (editorStore.enableHeadlessEditorExperimental) {
                  const resultElements =
                    editorStore.editor.commandSplitLayoutElements(
                      activeLayoutElement.source.id,
                      videoCreator.time,
                    );
                  elementToSelect = resultElements?.[0].top;
                } else {
                  await videoCreator.stackedLayoutManager.cutCurrentTrack(
                    activeLayoutElement,
                  );
                  elementToSelect = undefined;
                }
              } else {
                const resultElements = editorStore.splitElement(
                  activeElement.source.id,
                  videoCreator.time,
                );
                elementToSelect = resultElements?.[0];
              }
              if (elementToSelect) {
                videoSource.onNextStateChange(() => {
                  videoCreator.setActiveElements(elementToSelect!.id);
                });
              }
              const fadeProducer = new FadeProducer(videoCreator);
              await fadeProducer.tidyOriginalVideoOverlay();
            } else {
              videoCreator.cutCurrentTrack();
            }
          }}
        >
          <CutIcon strokeColor={isElementSelected ? '#F2D093' : '#848484'} />
          Split
        </Action>
        <Action
          aria-label="layer-up"
          disabled={!isElementSelected}
          onClick={handleLayerUp}
        >
          <TrackUpIcon
            strokeColor={isElementSelected ? '#F2D093' : '#848484'}
          />
          Layer Up
        </Action>
        <Action
          aria-label="layer-down"
          disabled={!isElementSelected}
          onClick={handleLayerDown}
        >
          <TrackDownIcon
            strokeColor={isElementSelected ? '#F2D093' : '#848484'}
          />
          Layer Down
        </Action>
        <Action
          disabled={!isElementSelected && videoCreator.selectedTrack === -1}
          onKeyDown={e => {
            if (e.code === 'Space') e.preventDefault();
          }}
          onClick={async e => {
            e.stopPropagation();
            const activeElement = videoCreator.getActiveElement();
            if (activeElement && videoCreator.selectedVolumeKeyPoint) {
              // Case: Deleting a volume key point
              videoCreator.deleteSelectedVolumeKeyPoint(
                activeElement.source.id,
              );
              return;
            }
            const activeLayoutElement = activeElement
              ? videoCreator.stackedLayoutManager.getLayoutElementFor(
                  activeElement,
                )
              : undefined;
            if (activeLayoutElement) {
              // Case: Deleting a layout element
              if (editorStore.enableHeadlessEditorExperimental) {
                await videoCreator.reframingModeManager.exitReframingMode();

                // Manually delete transcript without changing the source
                const elementId = moveUpToHiddenParent(
                  activeLayoutElement.source.id,
                  videoCreator.currentVideo?.extraElementData || {},
                );
                if (editorStore.editor.isOriginalVideoElement(elementId)) {
                  await videoCreator.videoTranscriptionProcessor.deleteOriginalVideoTrack(
                    elementId,
                    false,
                  );
                }

                editorStore.editor.commandDeleteLayoutElements(
                  activeLayoutElement.source.id,
                );
              } else {
                await videoCreator.stackedLayoutManager.deleteElementWithTranscription(
                  activeLayoutElement,
                );
              }
              videoCreator.setTime(activeLayoutElement.globalTime, true);
            } else if (activeElement) {
              // Case: Deleting a single element
              if (editorStore.enableHeadlessEditorExperimental) {
                if (
                  editorStore.editor.isOriginalVideoElement(
                    activeElement.source.id,
                  ) &&
                  !editorStore.useTranscriptSegments
                ) {
                  // Case: Deleting a video element with transcript; use legacy for now
                  await videoCreator.deleteElementWithTranscription(
                    activeElement.source.id,
                  );
                  await videoCreator.setDuration(null);
                } else {
                  // Case: Deleting other single elements
                  const initialSource = deepClone(videoSource.source);
                  const fadeProducer = new FadeProducer(
                    videoCreator,
                    activeElement,
                  );

                  await videoCreator.reframingModeManager.exitReframingMode();

                  editorStore.editor.commandRemoveElement(
                    activeElement.source.id,
                  );

                  // Legacy side-effects
                  videoSource.onNextStateChange(async () => {
                    await fadeProducer.removeElementOverlaysOnVideo([
                      activeElement.source.id,
                    ]);
                    videoCreator.trackManager.updateFrameLockedTracksAfterRearrange(
                      initialSource.elements,
                      videoSource.source.elements,
                    );
                    videoCreator.clearExtraElementData([
                      activeElement.source.id,
                    ]);
                  });
                }
              } else {
                await videoCreator.deleteElementWithTranscription(
                  activeElement.source.id,
                );
              }
              videoCreator.clearExtraElementData([activeElement.source.id]);
              if (activeElement.source.type === 'video') {
                videoCreator.setTime(activeElement.globalTime, true);
              }
            } else if (
              videoCreator.selectedTrack === KARAOKE_TRACK_NUMBER &&
              !editorStore.enableNewKaraokeArchitecture
            ) {
              // Case: Deleting karaoke track
              videoCreator.deleteKaraokeElements();
            } else if (videoCreator.selectedTrack > -1) {
              // Case: Deleting a track
              if (editorStore.enableHeadlessEditorWave3) {
                const initialSource = deepClone(videoSource.source);

                await videoCreator.reframingModeManager.exitReframingMode();

                const removedTrack = editorStore.editor.commandRemoveTrack(
                  String(videoCreator.selectedTrack),
                );

                // Legacy side-effects
                videoSource.onNextStateChange(async () => {
                  const removedIds =
                    removedTrack?.elements.map(el => el.id) || [];

                  const fadeProducer = new FadeProducer(videoCreator, null);
                  await fadeProducer.removeElementOverlaysOnVideo(removedIds);

                  videoCreator.trackManager.updateFrameLockedTracksAfterRearrange(
                    initialSource.elements,
                    videoSource.source.elements,
                  );

                  const idsWithPhotoHighlights = removedIds.filter(
                    (id: string) => videoCreator.hasPhotoHighlight(id),
                  );
                  if (idsWithPhotoHighlights.length) {
                    videoCreator.punchListManager.removePunchListItems(
                      idsWithPhotoHighlights,
                    );
                    videoCreator.createUndoPointForTranscription();
                  }
                  videoCreator.clearExtraElementData(removedIds);
                });
              } else {
                videoCreator.deleteElementsInTrack(videoCreator.selectedTrack);
              }
            }
            // Default to the next element when previous element is deleted
            videoCreator.sidebarOptions = SidebarOption.media;
            videoCreator.mediaSubMenu = MediaCard.photo;
            const elements = videoCreator.state?.elements;
            if (elements?.length) {
              const videoElement = elements.find(
                el => el.source.type === 'video',
              );

              videoSource.onNextStateChange(() => {
                if (videoElement?.source.id) {
                  videoCreator.setActiveElements(videoElement.source.id);
                }
              });
            }
          }}
        >
          <DeleteIcon
            strokeColor={
              !isElementSelected && videoCreator.selectedTrack === -1
                ? '#848484'
                : '#F2D093'
            }
          />
          Delete
        </Action>
      </LeftActions>

      <ToggleHide>
        <Action onClick={toggleTimeline}>
          <VisibilityStatus>
            {percentToPixel > 40 ? 'Hide Timeline' : 'Show Timeline'}
          </VisibilityStatus>
          <ArrowDown onClick={toggleTimeline} rotate={percentToPixel > 40}>
            <ArrowDownIcon />
          </ArrowDown>
        </Action>
      </ToggleHide>
    </Main>
  );
});

export default TimelineControls;
